// =============================================================================
// AccountMenu - the sidebar footer (Round 3 design review, item 6).
//
// The footer used to stack What's new, the user strip, My training, View as,
// Text size, Appearance and Collapse: 305px of a 900px screen, so an Owner saw
// 37% of the nav list. All of it now sits behind ONE compact row - avatar,
// name, role - that opens an upward menu. Collapse stays a small icon in the
// same row because it is used often and is not a personal preference.
//
// Presentational: every control calls back into Layout, which still owns the
// state, storage keys and permission gates exactly as before. This file only
// decides how they are shown. The one thing it owns is the What's new modal,
// through the shared useWhatsNew hook (same storage key as the old trigger).
//
// Accessibility: a menu button (aria-haspopup="menu", aria-expanded). Arrow
// Up / Down move between rows, Left / Right move within a row (text size,
// theme), Home / End jump, Escape closes and returns focus to the button,
// Tab or a click outside closes. Text size and theme keep the menu open so
// the user can step more than once; everything that leaves the menu closes it.
//
// Surface: the nav is #0a0a0a in BOTH themes, so the menu uses the fixed nav
// tokens (navBg / navText / navMuted / navLine), never theme tokens as text.
// =============================================================================
import { useState, useEffect, useRef, useCallback, useId } from "react";
import { C } from "../lib/tokens";
import { IconSettings, IconCheckSquare, IconEye, IconSparkles, IconLogOut } from "./icons";
import { useWhatsNew, WhatsNewModal } from "./WhatsNew";

// Raised surface for the menu, derived from the nav tokens so it stays a
// step lighter than the rail in both themes without a new literal.
const MENU_BG = "color-mix(in srgb, " + C.navText + " 7%, " + C.navBg + ")";
const HOVER_BG = "color-mix(in srgb, " + C.navText + " 10%, transparent)";
const ON_BG = "color-mix(in srgb, " + C.navText + " 16%, transparent)";
const TRACK_BG = "color-mix(in srgb, " + C.navBg + " 70%, transparent)";

// Hover and keyboard focus rings. Inline styles cannot express :hover or
// :focus-visible, so a tiny stylesheet is injected once (same pattern as the
// nav items in Layout).
if (typeof document !== "undefined" && !document.getElementById("pos-acct-style")) {
  const el = document.createElement("style");
  el.id = "pos-acct-style";
  el.textContent =
    ".pos-acct-hit:hover{background:" + HOVER_BG + " !important;}" +
    ".pos-acct-hit:focus-visible{outline:2px solid var(--brand-primary-mid,#1D9E75);outline-offset:-2px;}" +
    ".pos-acct-item:focus{background:" + HOVER_BG + ";outline:none;}" +
    ".pos-acct-item:focus-visible{outline:2px solid var(--brand-primary-mid,#1D9E75);outline-offset:-2px;}";
  document.head.appendChild(el);
}

// Sun / moon / screen glyphs for the theme control (moved here from Layout).
function ThemeGlyph({ kind, size = 12 }) {
  const p = { width: size, height: size, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (kind === "dark") {
    return (<svg {...p} aria-hidden="true"><path d="M13.2 9.6A5.6 5.6 0 0 1 6.4 2.8a5.6 5.6 0 1 0 6.8 6.8Z" /></svg>);
  }
  if (kind === "system") {
    return (<svg {...p} aria-hidden="true"><rect x="2" y="3" width="12" height="8" rx="1.2" /><path d="M6 13.5h4" /></svg>);
  }
  return (
    <svg {...p} aria-hidden="true">
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.4v1.3M8 13.3v1.3M1.4 8h1.3M13.3 8h1.3M3.4 3.4l.9.9M11.7 11.7l.9.9M12.6 3.4l-.9.9M4.3 11.7l-.9.9" />
    </svg>
  );
}

// Small stroke glyphs that the shared icon set does not have.
function Glyph({ d, size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}
const D_MINUS = "M4 8h8";
const D_PLUS = "M4 8h8M8 4v8";
const D_SELECTOR = "M5 6l3-3 3 3M5 10l3 3 3-3";
const D_CHEV_RIGHT = "M6 4l4 4-4 4";
// Sidebar panel with an arrow: collapse (pointing in) / expand (pointing out).
const D_COLLAPSE = "M2.5 3h11v10h-11zM6 3v10M11 6l-2 2 2 2";
const D_EXPAND = "M2.5 3h11v10h-11zM6 3v10M9 6l2 2-2 2";

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export default function AccountMenu({
  collapsed, hidden, isMobile, name, roleLabel, roleDot, previewing,
  showMySettings, showMyTraining, onNavigate,
  viewAs, textScale, onTextScale, theme, onSignOut, onToggleCollapse,
}) {
  const [open, setOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { unread, markSeen } = useWhatsNew();
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  // Which item to focus once the menu has rendered: "first" or "last".
  const focusOnOpen = useRef("first");
  const menuId = useId();

  const rowH = isMobile ? 44 : 34;

  // All focusable items, grouped by row (data-row), in DOM order.
  const rows = useCallback(() => {
    const items = Array.from(menuRef.current ? menuRef.current.querySelectorAll("[data-acct-item]") : []);
    const out = [];
    for (const it of items) {
      const key = it.getAttribute("data-row");
      const last = out[out.length - 1];
      if (last && key && last.key === key) last.items.push(it);
      else out.push({ key, items: [it] });
    }
    return out;
  }, []);

  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus && btnRef.current) btnRef.current.focus();
  }, []);

  // Focus the first (or last) row when the menu opens. For a segmented row
  // the selected segment takes focus, which is where a user expects to be.
  useEffect(() => {
    if (!open) return;
    const all = rows();
    if (all.length === 0) return;
    const row = focusOnOpen.current === "last" ? all[all.length - 1] : all[0];
    const pick = row.items.find((n) => n.getAttribute("aria-checked") === "true") || row.items[0];
    pick.focus();
  }, [open, rows]);

  // Outside click or tap closes. mousedown, not click, so the press that
  // starts a drag elsewhere also counts.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, [open]);

  // Collapsing or expanding the rail moves the anchor, and closing the phone
  // drawer hides it; close rather than leave a menu floating (or waiting
  // open in the drawer for next time).
  useEffect(() => { setOpen(false); }, [collapsed, hidden]);

  const onMenuKey = (e) => {
    const all = rows();
    const active = document.activeElement;
    const r = all.findIndex((row) => row.items.includes(active));
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); return; }
    if (e.key === "Tab") { setOpen(false); return; }
    const focusRow = (idx) => {
      const row = all[(idx + all.length) % all.length];
      const pick = row.items.find((n) => n.getAttribute("aria-checked") === "true") || row.items[0];
      pick.focus();
    };
    if (e.key === "ArrowDown") { e.preventDefault(); focusRow(r + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusRow(r < 0 ? all.length - 1 : r - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusRow(0); }
    else if (e.key === "End") { e.preventDefault(); focusRow(all.length - 1); }
    else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && r >= 0 && all[r].items.length > 1) {
      e.preventDefault();
      const items = all[r].items;
      const i = items.indexOf(active);
      const next = items[(i + (e.key === "ArrowRight" ? 1 : -1) + items.length) % items.length];
      next.focus();
    }
  };

  const onButtonKey = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      focusOnOpen.current = e.key === "ArrowUp" ? "last" : "first";
      setOpen(true);
    }
  };

  // An item that leaves the menu: close first, then act.
  const go = (fn) => () => { setOpen(false); fn(); };

  const itemStyle = {
    width: "100%", minHeight: rowH, display: "flex", alignItems: "center", gap: 10,
    padding: "0 10px", background: "transparent", border: "none", borderRadius: 6,
    color: C.navText, fontFamily: "inherit", fontSize: 13, fontWeight: 500,
    cursor: "pointer", textAlign: "left", boxSizing: "border-box",
  };
  const iconWrap = { display: "inline-flex", color: C.navMuted, flexShrink: 0, width: 16, justifyContent: "center" };
  const sep = <div role="separator" style={{ height: 1, background: C.navLine, margin: "4px 2px" }} />;
  const segBtn = (on) => ({
    flex: 1, minWidth: 0, height: isMobile ? 44 : 28,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    background: on ? ON_BG : "transparent",
    color: on ? C.navText : C.navMuted,
    border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
    fontSize: 12, fontWeight: on ? 600 : 500, fontVariantNumeric: "tabular-nums",
    boxShadow: on ? "0 1px 2px rgba(0,0,0,0.35)" : "none",
  });

  const avatar = (
    <span aria-hidden="true" style={{ position: "relative", flexShrink: 0, display: "inline-flex" }}>
      <span style={{
        width: 30, height: 30, borderRadius: 999,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "color-mix(in srgb, " + (roleDot || C.navMuted) + " 30%, " + C.navBg + ")",
        boxShadow: "inset 0 0 0 1px color-mix(in srgb, " + (roleDot || C.navMuted) + " 55%, transparent)",
        color: C.navText, fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
      }}>{initialsOf(name)}</span>
      {unread && (
        <span data-testid="whats-new-dot" title="Something new" style={{
          position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: 999,
          background: C.navAttention, boxShadow: "0 0 0 2px " + C.navBg,
        }} />
      )}
    </span>
  );

  const collapseBtn = onToggleCollapse ? (
    <button
      type="button"
      className="pos-acct-hit"
      onClick={onToggleCollapse}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      data-testid="sidebar-collapse"
      style={{
        width: 28, height: 28, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "transparent", color: C.navMuted, border: "none", borderRadius: 6, cursor: "pointer",
      }}
    >
      <Glyph d={collapsed ? D_EXPAND : D_COLLAPSE} size={16} />
    </button>
  ) : null;

  return (
    <div data-testid="account-footer" style={{
      position: "relative", flexShrink: 0,
      borderTop: "0.5px solid " + C.navLine,
      padding: collapsed ? "8px 0" : "8px 8px",
      display: "flex", alignItems: "center", gap: 4,
      flexDirection: collapsed ? "column" : "row",
    }}>
      <button
        ref={btnRef}
        type="button"
        className="pos-acct-hit"
        data-testid="account-menu-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={collapsed ? "Account menu, " + (name || "you") : undefined}
        title={collapsed ? (name || "Account") : undefined}
        onClick={() => { focusOnOpen.current = "first"; setOpen((o) => !o); }}
        onKeyDown={onButtonKey}
        style={{
          flex: collapsed ? "none" : 1, minWidth: 0,
          minHeight: isMobile ? 48 : 44,
          display: "flex", alignItems: "center", gap: 10,
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "6px" : "6px 8px",
          background: open ? HOVER_BG : "transparent",
          border: "none", borderRadius: 8, cursor: "pointer",
          color: C.navText, fontFamily: "inherit", textAlign: "left",
        }}
      >
        {avatar}
        {!collapsed && (
          <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.navText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>
              {name || "\u2014"}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              {/* Role name in navMuted with a role-tinted dot (#833): the nav is
                  #0a0a0a in both themes, so the name never takes a theme colour. */}
              <span data-testid="nav-role-label" style={{ fontSize: 11, color: C.navMuted, display: "flex", alignItems: "center", gap: 5, minWidth: 0, lineHeight: 1.2 }}>
                <span aria-hidden="true" data-testid="nav-role-dot" style={{ width: 6, height: 6, borderRadius: 999, background: roleDot || C.navMuted, flexShrink: 0 }} />
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{roleLabel}</span>
              </span>
              {/* While previewing another role the old amber View as button
                  was the only cue; keep one visible in the closed footer. */}
              {previewing && (
                <span data-testid="view-as-preview-chip" style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.navAttention, flexShrink: 0 }}>Preview</span>
              )}
            </span>
          </span>
        )}
        {!collapsed && (
          <span aria-hidden="true" style={{ display: "inline-flex", color: C.navMuted, flexShrink: 0 }}>
            <Glyph d={D_SELECTOR} size={14} />
          </span>
        )}
      </button>
      {collapseBtn}

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Account"
          data-testid="account-menu"
          onKeyDown={onMenuKey}
          style={{
            position: "absolute", bottom: "calc(100% + 6px)", left: 8,
            // A touch wider than the 220px rail on desktop so the theme
            // control keeps its labels; on a phone it fits the drawer.
            width: isMobile ? "calc(100% - 16px)" : 248,
            zIndex: 300, boxSizing: "border-box",
            background: MENU_BG, color: C.navText,
            border: "1px solid " + C.navLine, borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.3)",
            padding: 4,
            maxHeight: "calc(100vh - 96px)", overflowY: "auto",
          }}
        >
          {showMySettings && (
            <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
              onClick={go(() => onNavigate("/my-settings"))} style={itemStyle}>
              <span style={iconWrap}><IconSettings size={15} /></span>
              <span style={{ flex: 1 }}>My settings</span>
            </button>
          )}
          {showMyTraining && (
            <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
              data-testid="nav-my-training" onClick={go(() => onNavigate("/my-training"))} style={itemStyle}>
              <span style={iconWrap}><IconCheckSquare size={15} /></span>
              <span style={{ flex: 1 }}>My training</span>
            </button>
          )}
          {viewAs && (
            <>
              {/* Opens the portalled role sheet, not a nested menu: the phone
                  drawer is transformed and a native picker there does not
                  commit on iOS (#424). */}
              <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
                aria-haspopup="dialog" data-testid="view-as-open"
                onClick={go(viewAs.onOpen)} style={itemStyle}>
                <span style={iconWrap}><IconEye size={15} /></span>
                <span style={{ flexShrink: 0 }}>View as</span>
                <span style={{ flex: 1, minWidth: 0, textAlign: "right", fontSize: 12, color: viewAs.previewing ? C.navAttention : C.navMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {viewAs.current}{viewAs.previewing ? "" : " (you)"}
                </span>
                <span style={{ display: "inline-flex", color: C.navMuted, flexShrink: 0 }}><Glyph d={D_CHEV_RIGHT} size={12} /></span>
              </button>
              {viewAs.previewing && (
                <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
                  onClick={go(viewAs.onBack)} style={{ ...itemStyle, color: C.navAttention }}>
                  <span style={iconWrap} />
                  <span style={{ flex: 1 }}>Back to {viewAs.realRole}</span>
                </button>
              )}
            </>
          )}
          {(showMySettings || showMyTraining || viewAs) && sep}

          {/* Text size for THIS page on this device (lib/textScale). Shortcuts:
              Ctrl/Cmd + Alt + = / - / 0. */}
          <div role="group" aria-label="Text size" style={{ display: "flex", alignItems: "center", gap: 2, minHeight: rowH, padding: "0 4px 0 10px" }}
            title="Text size for this page on this device. Shortcuts: Ctrl + Alt + = / - / 0">
            <span style={{ flex: 1, fontSize: 13, color: C.navText, whiteSpace: "nowrap" }}>Text size</span>
            <span style={{ display: "flex", alignItems: "center", gap: 2, background: TRACK_BG, border: "1px solid " + C.navLine, borderRadius: 8, padding: 2 }}>
              {[[-1, "Smaller text on this page"], [0, "Reset this page to 100%"], [1, "Larger text on this page"]].map(([dir, label]) => (
                <button key={dir} type="button" role="menuitem" tabIndex={-1} data-acct-item data-row="text" className="pos-acct-item"
                  aria-label={label} title={label}
                  onClick={() => onTextScale(dir)}
                  style={{ ...segBtn(dir === 0 && textScale !== 1), flex: "none", width: dir === 0 ? 44 : (isMobile ? 40 : 26), color: dir === 0 && textScale === 1 ? C.navMuted : C.navText }}>
                  {dir === 0 ? Math.round(textScale * 100) + "%" : <Glyph d={dir < 0 ? D_MINUS : D_PLUS} size={12} />}
                </button>
              ))}
            </span>
          </div>

          {/* Theme. Same gate as before (Layout passes null when dark mode is
              neither generally available nor a preview this user may try). */}
          {theme && (!theme.previewOn ? (
            <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
              onClick={theme.onTryDark} title="Dark mode is a preview: some screens may still show light patches" style={itemStyle}>
              <span style={iconWrap}><ThemeGlyph kind="dark" size={14} /></span>
              <span style={{ flex: 1 }}>Try dark mode</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: C.navMuted }}>PREVIEW</span>
            </button>
          ) : (
            <>
              {/* Phones stack the label over the control: the drawer is 240px
                  and three 44px-tall labelled segments beside a label crush. */}
              <div style={{ padding: "4px 4px 4px 10px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 4 : 8 }}>
                <span style={{ fontSize: 13, color: C.navText, flexShrink: 0, padding: isMobile ? "6px 0 2px" : 0 }}>Theme</span>
                <div role="group" aria-label="Appearance" style={{
                  flex: 1, minWidth: 0, display: "flex", gap: 2,
                  background: TRACK_BG, border: "1px solid " + C.navLine, borderRadius: 8, padding: 2,
                }}>
                  {[["light", "Light"], ["dark", "Dark"], ["system", "Auto"]].map(([v, label]) => {
                    const on = theme.pref === v;
                    return (
                      <button key={v} type="button" role="menuitemradio" aria-checked={on} tabIndex={-1}
                        data-acct-item data-row="theme" className="pos-acct-item"
                        title={"Appearance: " + label}
                        onClick={() => theme.onPick(v)} style={segBtn(on)}>
                        <ThemeGlyph kind={v} size={12} />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {!theme.ready && (
                <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
                  onClick={theme.onLeavePreview} title="Leave the dark mode preview" style={{ ...itemStyle, color: C.navMuted, fontSize: 12 }}>
                  <span style={iconWrap} />
                  <span style={{ flex: 1 }}>Leave preview</span>
                </button>
              )}
            </>
          ))}
          {sep}

          <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
            data-testid="whats-new-open"
            onClick={go(() => { setWhatsNewOpen(true); markSeen(); })} style={itemStyle}>
            <span style={iconWrap}><IconSparkles size={15} /></span>
            <span style={{ flex: 1 }}>What's new</span>
            {unread && <span aria-label="Unread" style={{ width: 7, height: 7, borderRadius: 999, background: C.navAttention, flexShrink: 0 }} />}
          </button>
          {sep}
          <button type="button" role="menuitem" tabIndex={-1} data-acct-item className="pos-acct-item"
            onClick={go(onSignOut)} style={itemStyle}>
            <span style={iconWrap}><IconLogOut size={15} /></span>
            <span style={{ flex: 1 }}>Sign out</span>
          </button>
        </div>
      )}

      {whatsNewOpen && <WhatsNewModal onClose={() => setWhatsNewOpen(false)} />}
    </div>
  );
}
